import React, { useState, useEffect, useCallback } from 'react';
import './MealsManager.css';
import { DEPARTMENTS, titleCase } from './constants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function MealsManager({ token, isAdmin, onMealsUpdated, onAuthError, onFormStateChange }) {
  const [meals, setMeals] = useState([]);
  const [defaultMeals, setDefaultMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingMeal, setEditingMeal] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('my-meals');

  // Notify parent when form state changes
  useEffect(() => {
    if (onFormStateChange) {
      const isFormOpen = showAddForm || editingMeal !== null;
      onFormStateChange(isFormOpen);
    }
  }, [showAddForm, editingMeal, onFormStateChange]);

  const loadMeals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/meals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during meals load - logging out');
        if (onAuthError) onAuthError();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setMeals(data.userMeals || []);
        setDefaultMeals(data.defaultMeals || []);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to load meals');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
    setLoading(false);
  }, [token, onAuthError]);

  // Load meals from API
  useEffect(() => {
    loadMeals();
  }, [loadMeals]);

  const handleSaveMeal = async (meal, isNew = false) => {
    setError('');
    try {
      const isDefaultMeal = activeTab === 'default-meals';
      let url, method;
      
      if (isDefaultMeal) {
        // Admin endpoints for default meals
        url = isNew
          ? `${API_URL}/api/meals/defaults`
          : `${API_URL}/api/meals/defaults/${encodeURIComponent(meal.originalName || meal.name)}`;
        method = isNew ? 'POST' : 'PUT';
      } else {
        // User meal endpoints
        url = isNew
          ? `${API_URL}/api/meals`
          : `${API_URL}/api/meals/${encodeURIComponent(meal.originalName || meal.name)}`;
        method = isNew ? 'POST' : 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: meal.name,
          ingredients: meal.ingredients
        })
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during meal save - logging out');
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        await loadMeals();
        setEditingMeal(null);
        setShowAddForm(false);
        if (onMealsUpdated) {
          onMealsUpdated();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save meal');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  const handleDeleteMeal = async (mealName) => {
    if (!window.confirm(`Are you sure you want to delete "${mealName}"?`)) {
      return;
    }

    setError('');
    try {
      const isDefaultMeal = activeTab === 'default-meals';
      // Admin endpoints use /api/meals/defaults/:name, user endpoints use /api/meals/:name
      const url = isDefaultMeal
        ? `${API_URL}/api/meals/defaults/${encodeURIComponent(mealName)}`
        : `${API_URL}/api/meals/${encodeURIComponent(mealName)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during meal delete - logging out');
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        await loadMeals();
        if (onMealsUpdated) {
          onMealsUpdated();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete meal');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  const handleCopyToMyMeals = async (meal) => {
    setError('');
    try {
      // POST to /api/meals to create a user meal
      const response = await fetch(`${API_URL}/api/meals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: meal.name,
          ingredients: meal.ingredients
        })
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during meal copy - logging out');
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        await loadMeals();
        if (onMealsUpdated) {
          onMealsUpdated();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to copy meal');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  const currentMeals = activeTab === 'default-meals' ? defaultMeals : meals;
  const canEdit = activeTab === 'my-meals' || (activeTab === 'default-meals' && isAdmin);

  if (loading) {
    return (
      <div className="meals-manager">
        <div className="loading-screen">
          <h2>Loading meals...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="meals-manager">
      <div className="meals-tabs">
        <button
          className={`tab-button ${activeTab === 'my-meals' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-meals')}
        >
          My Meals ({meals.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'default-meals' ? 'active' : ''}`}
          onClick={() => setActiveTab('default-meals')}
        >
          Search for Meals ({defaultMeals.length})
          {isAdmin && <span className="admin-badge">Admin</span>}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={() => setError('')} className="dismiss-button">✕</button>
        </div>
      )}

      <div className="meals-actions">
        {canEdit && (
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingMeal({
                name: '',
                ingredients: [{ ingredient: '', quantity: '', quantity_type: '', department: 'grocery' }],
                isNew: true
              });
            }}
            className="add-meal-button"
          >
            + Add New Meal
          </button>
        )}
      </div>

      {(showAddForm || editingMeal) && (
        <MealEditor
          meal={editingMeal}
          onSave={(meal) => handleSaveMeal(meal, editingMeal?.isNew)}
          onCancel={() => {
            setEditingMeal(null);
            setShowAddForm(false);
          }}
        />
      )}

      <div className="meals-list">
        {currentMeals.length === 0 ? (
          <div className="no-meals">
            <p>
              {activeTab === 'my-meals' 
                ? "You haven't created any custom meals yet."
                : "No default meals available."}
            </p>
            {activeTab === 'my-meals' && defaultMeals.length > 0 && (
              <p className="hint">
                You can copy meals from the Search for Meals tab to customize them.
              </p>
            )}
          </div>
        ) : (
          currentMeals.sort((a, b) => a.name.localeCompare(b.name)).map((meal, index) => (
            <MealCard
              key={`${meal.name}-${index}`}
              meal={meal}
              canEdit={canEdit}
              showCopyButton={activeTab === 'default-meals' && !isAdmin}
              isAlreadyAdded={activeTab === 'default-meals' && meals.some(m => m.name === meal.name)}
              onEdit={() => setEditingMeal({ ...meal, originalName: meal.name })}
              onDelete={() => handleDeleteMeal(meal.name)}
              onCopy={() => handleCopyToMyMeals(meal)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MealCard({ meal, canEdit, showCopyButton, isAlreadyAdded, onEdit, onDelete, onCopy }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="meal-card">
      {showCopyButton && (
        <button
          onClick={(e) => { e.stopPropagation(); if (!isAlreadyAdded) onCopy(); }}
          className={`copy-button-corner ${isAlreadyAdded ? 'already-added' : ''}`}
          title={isAlreadyAdded ? "Already in My Meals" : "Add to My Meals"}
          disabled={isAlreadyAdded}
        >
          {isAlreadyAdded ? '✓' : '+'}
        </button>
      )}
      <div className="meal-card-header" onClick={() => setExpanded(!expanded)}>
        <h3 className="meal-card-title">
          {meal.name}
          {/* TODO: Fix this. {!showCopyButton && meal.isDefault && <span className="default-badge">Default</span>} */}
        </h3>
        <div className="meal-card-actions">
          <span className="ingredient-count">
            {meal.ingredients?.length || 0} ingredients
          </span>
          <span className="expand-icon">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>
      
      {expanded && (
        <div className="meal-card-content">
          <div className="ingredients-list">
            {meal.ingredients?.map((ing, index) => (
              <div key={index} className="ingredient-row">
                <span className="ingredient-name">{ing.ingredient}</span>
                <span className="ingredient-quantity">
                  {ing.quantity} {ing.quantity_type}
                </span>
                <span className="ingredient-department">{ing.department}</span>
              </div>
            ))}
          </div>
          <div className="meal-card-buttons">
            {canEdit && (
              <>
                <button onClick={onEdit} className="edit-button">✏️ Edit</button>
                <button onClick={onDelete} className="delete-button">🗑️ Delete</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MealEditor({ meal, onSave, onCancel }) {
  const [name, setName] = useState(meal?.name || '');
  const [ingredients, setIngredients] = useState(
    meal?.ingredients?.length > 0
      ? meal.ingredients
      : [{ ingredient: '', quantity: '', quantity_type: '', department: 'grocery' }]
  );
  const [error, setError] = useState('');

  // Lock body scroll when modal is open
  useEffect(() => {
    // Save original body overflow
    const originalOverflow = document.body.style.overflow;

    // Lock the body scroll without using position: fixed
    // which can cause issues with touch events and click detection
    document.body.style.overflow = 'hidden';

    // Cleanup: restore body overflow when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleAddIngredient = () => {
    setIngredients([
      ...ingredients,
      { ingredient: '', quantity: '', quantity_type: '', department: 'grocery' }
    ]);
  };

  const handleRemoveIngredient = (index) => {
    if (ingredients.length === 1) {
      setError('A meal must have at least one ingredient');
      return;
    }
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleIngredientChange = (index, field, value) => {
    const updated = [...ingredients];
    updated[index] = { ...updated[index], [field]: value };
    setIngredients(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Meal name is required');
      return;
    }

    const validIngredients = ingredients.filter(ing => ing.ingredient.trim());
    if (validIngredients.length === 0) {
      setError('At least one ingredient is required');
      return;
    }

    onSave({
      name: name.trim(),
      ingredients: validIngredients,
      originalName: meal?.originalName
    });
  };

  return (
    <div className="meal-editor-overlay">
      <div className="meal-editor">
        <h2>{meal?.isNew ? 'Add New Meal' : 'Edit Meal'}</h2>
        
        {error && (
          <div className="editor-error">
            <p>❌ {error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-group">
            <label htmlFor="meal-name">Meal Name</label>
            <input
              type="text"
              id="meal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter meal name"
              autoFocus
              autoComplete="one-time-code"
            />
          </div>

          <div className="ingredients-editor">
            <h3>Ingredients</h3>
            {ingredients.map((ing, index) => (
              <div key={index} className="ingredient-editor-row">
                <input
                  type="text"
                  value={ing.ingredient}
                  onChange={(e) => handleIngredientChange(index, 'ingredient', e.target.value)}
                  placeholder="Ingredient name"
                  className="ingredient-input"
                  autoComplete="one-time-code"
                />
                <input
                  type="text"
                  value={ing.quantity}
                  onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                  placeholder="Qty"
                  className="quantity-input"
                  autoComplete="one-time-code"
                  inputMode="decimal"
                />
                <input
                  type="text"
                  value={ing.quantity_type}
                  onChange={(e) => handleIngredientChange(index, 'quantity_type', e.target.value)}
                  placeholder="Unit"
                  className="unit-input"
                  autoComplete="one-time-code"
                />
                <select
                  value={ing.department}
                  onChange={(e) => handleIngredientChange(index, 'department', e.target.value)}
                  className="department-select"
                >
                  {DEPARTMENTS.map(dept => (
                    <option key={dept} value={dept}>
                      {titleCase(dept)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleRemoveIngredient(index)}
                  className="remove-ingredient-button"
                  title="Remove ingredient"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddIngredient}
              className="add-ingredient-button"
            >
              + Add Ingredient
            </button>
          </div>

          <div className="editor-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save Meal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MealsManager;
